import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2, FiPackage } from "react-icons/fi";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Modal from "../components/ui/Modal";
import Table from "components/ui/Table";
import FilterBar from "components/ui/FilterBar";
import PackageEdit from "components/package/Edit";
import {
  useGetPackagesQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useRestorePackageMutation,
} from "apis/package";
import { useContractorCompaniesQuery } from "apis/contractorCompany";
import { useGetProductsQuery } from "apis/products";
import moment from "moment-timezone";

// Packages — a contractor company's products bundled at one fixed price.
// Agents buy them in the marketing cart, which expands them into their
// products. Companies also manage their own in the contractor portal.

const IN_HOUSE = "inHouse";

const Packages = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState("active");
  const [companyFilter, setCompanyFilter] = useState("");

  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetPackagesQuery();
  const { data: contractorCompanies = [] } = useContractorCompaniesQuery();
  const { data: allProducts = [] } = useGetProductsQuery({ type: "marketing" });

  const { mutateAsync: createPackage, isPending: isCreateLoading } =
    useCreatePackageMutation();
  const { mutateAsync: updatePackage, isPending: isUpdateLoading } =
    useUpdatePackageMutation();
  const { mutate: deletePackage, isPending: isDeleteLoading } =
    useDeletePackageMutation();
  const { mutate: restorePackage, isPending: isRestoreLoading } =
    useRestorePackageMutation();

  const companyOptions = useMemo(
    () => [
      { value: IN_HOUSE, label: "In-house" },
      ...(contractorCompanies || []).map((c) => ({
        value: String(c._id),
        label: c.name,
      })),
    ],
    [contractorCompanies],
  );

  const filteredData = useMemo(
    () =>
      (data || []).filter((pkg) => {
        if (viewMode === "active" ? pkg.isDeleted : !pkg.isDeleted)
          return false;
        if (companyFilter) {
          const companyId = String(pkg.contractorCompanyId?._id || IN_HOUSE);
          if (companyId !== companyFilter) return false;
        }
        return true;
      }),
    [data, viewMode, companyFilter],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Package Name",
        isFilter: true,
        minWidth: "170px",
      },
      {
        accessorKey: "contractorCompanyId",
        header: "Contractor Company",
        cell: (row) => (
          <span className="truncate inline-block max-w-[180px]">
            {row.contractorCompanyId?.name || "In-house"}
          </span>
        ),
        isFilter: false,
        minWidth: "160px",
      },
      {
        accessorKey: "price",
        header: "Price",
        cell: (row) => <span className="font-medium">${row.price}</span>,
        isFilter: false,
      },
      {
        accessorKey: "productIds",
        header: "Products",
        cell: (row) => {
          const names = (row.productIds || []).map((p) => p?.name || String(p));
          return (
            <span
              className="truncate inline-block max-w-[240px]"
              title={names.join(", ")}
            >
              {names.join(", ") || "-"}
            </span>
          );
        },
        isFilter: false,
        minWidth: "200px",
      },
      {
        accessorKey: "offices",
        header: "Offices",
        cell: (row) => {
          const names = (row.offices || []).map((o) => o?.name || String(o));
          if (!names.length) {
            return (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-lightgray text-darkgray">
                All offices
              </span>
            );
          }
          return (
            <span
              className="truncate inline-block max-w-[200px]"
              title={names.join(", ")}
            >
              {names.join(", ")}
            </span>
          );
        },
        isFilter: false,
        minWidth: "160px",
      },
      ...(viewMode === "archive"
        ? [
            {
              accessorKey: "deletedAt",
              header: "Deleted Date",
              cell: (row) => (
                <span className="text-sm">
                  {row.deletedAt
                    ? moment(row.deletedAt)
                        .tz("Australia/Sydney")
                        .format("D MMM YYYY")
                    : "-"}
                </span>
              ),
              isFilter: false,
            },
          ]
        : []),
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Package"
            >
              <FiEdit2 fontSize={20} />
            </button>
            {viewMode === "active" ? (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsDeleteOpen(true);
                }}
                className="delete-icon"
                title="Delete Package"
              >
                <FiTrash2 fontSize={20} />
              </button>
            ) : (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsRestoreOpen(true);
                }}
                className="view-icon"
                title="Restore Package"
              >
                <MdOutlineSettingsBackupRestore fontSize={22} color="green" />
              </button>
            )}
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [viewMode],
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading packages: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedPackage = data?.find((pkg) => pkg._id === selectedId);

  const handleCreateSubmit = async (formData) => {
    await createPackage(formData);
    setIsAddOpen(false);
  };

  const handleEditSubmit = async (formData) => {
    await updatePackage({ _id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const AddPackageButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Package
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Package"
        className="max-w-2xl"
      >
        <PackageEdit
          contractorCompanies={contractorCompanies}
          products={allProducts}
          isSaving={isCreateLoading}
          onSubmit={handleCreateSubmit}
          onCancel={() => setIsAddOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Package"
        className="max-w-2xl"
      >
        <PackageEdit
          key={selectedId}
          initialData={selectedPackage}
          contractorCompanies={contractorCompanies}
          products={allProducts}
          isSaving={isUpdateLoading}
          onSubmit={handleEditSubmit}
          onCancel={() => setIsEditOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Package"
      >
        <div>
          <p className="mb-4">
            Delete "{selectedPackage?.name}"? Agents stop being offered it, and
            package rules that name it skip it.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={() =>
                deletePackage(selectedId, {
                  onSuccess: () => setIsDeleteOpen(false),
                })
              }
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        title="Restore Package"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to restore "{selectedPackage?.name}"?
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsRestoreOpen(false)}
              className="gray-button"
              disabled={isRestoreLoading}
            >
              No
            </button>
            <button
              onClick={() =>
                restorePackage(selectedId, {
                  onSuccess: () => setIsRestoreOpen(false),
                })
              }
              className="black-button"
              disabled={isRestoreLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div className="flex gap-2 bg-lightgray rounded-lg p-1 border border-mediumgray self-start">
            <button
              onClick={() => setViewMode("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "active"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiPackage className="w-4 h-4" />
              <span>Active</span>
            </button>
            <button
              onClick={() => setViewMode("archive")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "archive"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiTrash2 className="w-4 h-4" />
              <span>Archive</span>
            </button>
          </div>

          <FilterBar
            className="w-full sm:w-auto [&>div]:flex-1 [&>div]:min-w-0 sm:[&>div]:flex-none"
            showClear={!!companyFilter}
            onClear={() => setCompanyFilter("")}
            filters={[
              {
                key: "company",
                value: companyFilter,
                onChange: setCompanyFilter,
                allLabel: "All Companies",
                options: companyOptions,
                minWidth: "200px",
              },
            ]}
          />
        </div>

        <Table
          title="Packages"
          columns={columns}
          data={filteredData}
          pageSize={pageSize}
          ButtonCmp={viewMode === "active" ? AddPackageButton : undefined}
          onRowClick={(row) => {
            setSelectedId(row._id);
            setIsEditOpen(true);
          }}
          emptyText="No packages found"
        />
      </div>
    </>
  );
};

export default Packages;
